import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [expandedService, setExpandedService] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [newsletterStatus, setNewsletterStatus] = useState<{ type: 'success' | 'error' | 'loading'; message: string } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus('submitting');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('company', formData.company);
      formBody.append('phone', formData.phone);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d4mtcga32n60g3bh85t0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setFormStatus('success');
        setFormData({ name: '', email: '', company: '', phone: '', message: '' });
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus('idle'), 5000);
      }
    } catch (error) {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 5000);
    }
  };

  const handleServiceClick = (index: number) => {
    setExpandedService(index);
    setTimeout(() => {
      const servicesSection = document.getElementById('services');
      if (servicesSection) {
        servicesSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const services = [
    {
      number: '01',
      title: 'GRCaaS - Governance, Risk & Compliance as a Service',
      description: 'Comprehensive managed GRC operations combining governance frameworks, Virtual CISO leadership, and continuous training—delivered as a single, integrated service.',
      items: [
        'Enterprise Governance & Board Risk Oversight',
        'Enterprise, Operational & Cyber Risk Management',
        'Regulatory & Standards Compliance (ISO, NIST, COBIT)',
        'Policy, Control, Issue & Audit Management',
        'Virtual CISO (vCISO) Leadership',
        'Executive & Board-Level Reporting Dashboards',
        'Continuous Cybersecurity & Compliance Training',
        'Third-Party Risk Management'
      ],
      tagline: 'Leadership, people, and controls working together to manage risk and meet regulatory expectations.'
    },
    {
      number: '02',
      title: 'Cybersecurity Services',
      description: 'Protecting your digital environment with intelligent, modern, and layered security defences.',
      items: [
        'Threat Monitoring & Detection',
        'Vulnerability Assessments',
        'Penetration Testing (Network, Web, Mobile, Cloud, API)',
        'SOC Setup & Advisory',
        'Endpoint Security & Hardening',
        'Network & Cloud Security Architecture',
        'Incident Response Planning'
      ],
      tagline: 'Security that adapts, evolves, and defends.'
    },
    {
      number: '03',
      title: 'IT Governance & Compliance',
      description: 'Ensuring your organization aligns with both local and global regulatory standards.',
      items: [
        'IT Governance Frameworks (COBIT, ITIL)',
        'ISO 27001 Readiness & Implementation',
        'Data Privacy & Protection Compliance',
        'Ghana Cybersecurity Act 1038 Guidance',
        'Bank of Ghana Technology & Cybersecurity Directives',
        'Policy Development & Review',
        'Compliance Audits & Health Checks'
      ],
      tagline: 'We help organizations build a governance structure that supports growth, resilience, and accountability.'
    },
    {
      number: '04',
      title: 'Security Awareness & Capacity Building',
      description: 'Empowering your team with knowledge and skills to become your first line of defense.',
      items: [
        'Cybersecurity Awareness Training',
        'Phishing Simulation Campaigns',
        'Role-Based Security Training',
        'Executive Security Briefings',
        'Technical Skills Development',
        'Security Culture Programs'
      ],
      tagline: 'Building a security-conscious workforce.'
    },
    {
      number: '05',
      title: 'Consultancy & Executive Advisory',
      description: 'Strategic guidance for boards, executives, and technology leaders.',
      items: [
        'Cybersecurity Program Development',
        'Digital Transformation Advisory',
        'Technology Governance Insight',
        'Fintech & Payment Security Guidance',
        'Strategic Architecture & Roadmaps',
        'Board-Level Cyber Briefings'
      ],
      tagline: 'We help leaders build secure, scalable digital ecosystems.'
    }
  ];

  const industries = [
    { name: 'Banking & Financial Services', icon: 'ri-bank-line' },
    { name: 'Fintech & Payment', icon: 'ri-wallet-3-line' },
    { name: 'Telecom & Technology', icon: 'ri-signal-tower-line' },
    { name: 'Government & Public Sector', icon: 'ri-government-line' },
    { name: 'Healthcare', icon: 'ri-hospital-line' },
    { name: 'Insurance', icon: 'ri-shield-check-line' },
    { name: 'Energy & Infrastructure', icon: 'ri-flashlight-line' },
    { name: 'Education', icon: 'ri-book-open-line' },
    { name: 'Corporate Enterprises', icon: 'ri-building-line' }
  ];

  const whyChoose = [
    {
      icon: 'ri-map-pin-line',
      title: 'Ghana-Centred, Africa-Focused',
      description: 'We operate with deep knowledge of Ghana\'s regulatory environment while serving clients across Africa.'
    },
    {
      icon: 'ri-file-shield-2-line',
      title: 'Governance & Compliance Authority',
      description: 'We align organizations with CSA Ghana, Bank of Ghana directives, Data Protection Act, and global standards.'
    },
    {
      icon: 'ri-search-eye-line',
      title: 'Evidence-Based Investigations',
      description: 'Our digital forensics team provides accurate, defensible reporting.'
    },
    {
      icon: 'ri-checkbox-multiple-line',
      title: 'Vendor-Neutral Approach',
      description: 'We recommend what is best for your environment — not what vendors sell.'
    },
    {
      icon: 'ri-team-line',
      title: 'Experienced Leadership',
      description: 'Decades of combined experience in cybersecurity, risk, technology management, and forensics.'
    },
    {
      icon: 'ri-lock-password-line',
      title: 'Discreet, Secure & Confidential',
      description: 'Your data and your trust are our top priorities.'
    }
  ];

  // Newsletter subscription handler
  const handleNewsletterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const emailInput = form.querySelector('input[name="email"]') as HTMLInputElement;
    const email = emailInput.value.trim();

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setNewsletterStatus({ type: 'error', message: 'Please enter a valid email address' });
      return;
    }

    setNewsletterStatus({ type: 'loading', message: 'Subscribing...' });

    try {
      const formData = new URLSearchParams();
      formData.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d4mtj9it20jieso0q5jg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        setNewsletterStatus({ type: 'success', message: 'Successfully subscribed!' });
        emailInput.value = '';
        setTimeout(() => setNewsletterStatus(null), 3000);
      } else {
        setNewsletterStatus({ type: 'error', message: 'Subscription failed. Please try again.' });
      }
    } catch (error) {
      setNewsletterStatus({ type: 'error', message: 'Network error. Please try again.' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/f20346e8553efa0abaa27ccf5800486f.jpg"
            alt="Cybersecurity Background"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 text-center">
          <div className="mb-8">
            <img
              src="https://static.readdy.ai/image/3ca3a64fdbdd1a27f61611d5a6ab11d4/0b305659fb07b7246329e00bf7bbf5b5.png"
              alt="VAS Cyber Logo"
              className="w-48 h-48 mx-auto mb-6"
            />
          </div>
          
          <h1 className="text-6xl md:text-7xl font-bold text-white mb-6 leading-tight">
            Your Trusted Partner in <span className="bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">Digital Protection</span>
          </h1>
          
          <p className="text-xl md:text-2xl text-gray-200 mb-4 max-w-4xl mx-auto font-light">
            VAS Cyber is a Ghana-centred, Pan-African cybersecurity and digital risk management company delivering world-class protection, governance, and advisory services.
          </p>
          
          <p className="text-lg text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed">
            We empower organizations to predict, prevent, detect, and respond to cyber threats while ensuring regulatory alignment, operational resilience, and technology-driven growth.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">
            <a
              href="#contact"
              className="px-8 py-4 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-semibold rounded-full hover:shadow-lg hover:shadow-emerald-500/50 transition-all duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer"
            >
              Get Started <i className="ri-arrow-right-line"></i>
            </a>
            <a
              href="#contact"
              className="px-8 py-4 border-2 border-emerald-400 text-emerald-400 font-semibold rounded-full hover:bg-emerald-400 hover:text-white transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Contact Us
            </a>
          </div>

          <div className="flex flex-wrap gap-6 justify-center">
            <div className="bg-white/10 backdrop-blur-md rounded-2xl px-8 py-4 border border-white/20">
              <div className="text-3xl font-bold text-emerald-400 mb-1">500+</div>
              <div className="text-sm text-gray-300">Clients Protected</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-2xl px-8 py-4 border border-white/20">
              <div className="text-3xl font-bold text-emerald-400 mb-1">99.9%</div>
              <div className="text-sm text-gray-300">Threat Detection</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-2xl px-8 py-4 border border-white/20">
              <div className="text-3xl font-bold text-emerald-400 mb-1">24/7</div>
              <div className="text-sm text-gray-300">Security Monitoring</div>
            </div>
          </div>
        </div>
      </section>

      {/* GRCaaS Feature Section */}
      <section className="py-24 bg-gradient-to-br from-emerald-900 via-gray-900 to-cyan-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-emerald-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-500 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-block bg-gradient-to-r from-emerald-500 to-cyan-500 text-white text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-full mb-6">
              Featured Service
            </div>
            <h2 className="text-5xl md:text-6xl font-bold mb-6">GRCaaS</h2>
            <p className="text-2xl md:text-3xl font-light mb-4">Governance, Risk & Compliance as a Service</p>
            <div className="w-32 h-1 bg-gradient-to-r from-emerald-400 to-cyan-400 mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 hover:bg-white/15 transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">GRC Management</h3>
              <ul className="space-y-3 text-gray-200">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-emerald-400 flex-shrink-0 mt-1"></i>
                  <span>Enterprise Governance & Board Risk Oversight</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-emerald-400 flex-shrink-0 mt-1"></i>
                  <span>Regulatory & Standards Compliance</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-emerald-400 flex-shrink-0 mt-1"></i>
                  <span>Policy & Control Management</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-emerald-400 flex-shrink-0 mt-1"></i>
                  <span>Executive Reporting Dashboards</span>
                </li>
              </ul>
            </div>

            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 hover:bg-white/15 transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-user-star-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Virtual CISO (vCISO)</h3>
              <ul className="space-y-3 text-gray-200">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-cyan-400 flex-shrink-0 mt-1"></i>
                  <span>Define & Execute Cybersecurity Strategy</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-cyan-400 flex-shrink-0 mt-1"></i>
                  <span>Align Cyber Risk to Business Objectives</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-cyan-400 flex-shrink-0 mt-1"></i>
                  <span>Lead Incident Response Governance</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-cyan-400 flex-shrink-0 mt-1"></i>
                  <span>Board & Executive Briefings</span>
                </li>
              </ul>
            </div>

            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 hover:bg-white/15 transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-graduation-cap-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Training & Development</h3>
              <ul className="space-y-3 text-gray-200">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-emerald-400 flex-shrink-0 mt-1"></i>
                  <span>Executive & Board Training</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-emerald-400 flex-shrink-0 mt-1"></i>
                  <span>Management & Operational Training</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-emerald-400 flex-shrink-0 mt-1"></i>
                  <span>Staff Awareness Programs</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-emerald-400 flex-shrink-0 mt-1"></i>
                  <span>Tabletop Exercises & Simulations</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-gradient-to-r from-white/10 to-white/5 backdrop-blur-md rounded-2xl p-10 border border-white/20">
            <h3 className="text-3xl font-bold mb-8 text-center">Service Delivery Model</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
              {[
                { icon: 'ri-search-line', label: 'Assess', desc: 'Baseline GRC & Cyber Maturity' },
                { icon: 'ri-pencil-ruler-2-line', label: 'Design', desc: 'Framework & Roadmap' },
                { icon: 'ri-user-star-line', label: 'Lead', desc: 'vCISO-Led Governance' },
                { icon: 'ri-settings-3-line', label: 'Operate', desc: 'Managed GRC Operations' },
                { icon: 'ri-graduation-cap-line', label: 'Train', desc: 'Continuous Training' },
                { icon: 'ri-line-chart-line', label: 'Improve', desc: 'Ongoing Maturity' }
              ].map((step, index) => (
                <div key={index} className="text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className={`${step.icon} text-3xl text-white`}></i>
                  </div>
                  <h4 className="text-lg font-bold mb-2">{step.label}</h4>
                  <p className="text-sm text-gray-300">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-16 text-center">
            <p className="text-2xl font-light mb-8">
              Protect Today. Prepare Tomorrow.
            </p>
            <a
              href="#contact"
              className="inline-block px-10 py-4 bg-white text-gray-900 font-bold rounded-full hover:shadow-2xl hover:shadow-emerald-500/50 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Engage VAS Cyber for GRCaaS
            </a>
          </div>
        </div>
      </section>

      {/* Core Strengths */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">Our Core Strengths</h2>
            <div className="w-20 h-1 bg-gradient-to-r from-emerald-500 to-cyan-500 mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: 'ri-shield-check-line', title: 'GRCaaS with vCISO Leadership', desc: 'Integrated governance, risk, compliance, and executive leadership', index: 0 },
              { icon: 'ri-radar-line', title: 'Cybersecurity Defence & Monitoring', desc: 'Intelligent, modern, and layered security defences', index: 1 },
              { icon: 'ri-file-list-3-line', title: 'IT Governance & Compliance', desc: 'Align with local and global regulatory standards', index: 2 },
              { icon: 'ri-lightbulb-line', title: 'Security Awareness & Capacity Building', desc: 'Empower your team as the first line of defense', index: 3 },
              { icon: 'ri-user-star-line', title: 'Executive Advisory & Consultancy', desc: 'Strategic guidance for technology leaders', index: 4 }
            ].map((strength, index) => (
              <div
                key={index}
                onClick={() => handleServiceClick(strength.index)}
                className="group bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 hover:shadow-2xl hover:shadow-emerald-500/20 transition-all duration-300 hover:-translate-y-2 border border-gray-700 cursor-pointer"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <i className={`${strength.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">{strength.title}</h3>
                <p className="text-gray-300 leading-relaxed">{strength.desc}</p>
                <div className="mt-6 flex items-center text-emerald-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span className="text-sm font-semibold">Learn More</span>
                  <i className="ri-arrow-right-line ml-2"></i>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-16">
            <p className="text-2xl text-gray-700 font-light mb-6">Your security challenges demand expertise.</p>
            <p className="text-3xl font-bold text-gray-900">VAS Cyber delivers excellence.</p>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <div className="inline-block bg-emerald-500 text-white text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-full mb-6">
                Who We Are
              </div>
              <h2 className="text-5xl font-bold text-gray-900 mb-8 leading-tight">
                Leading Africa's Cyber Defense
              </h2>
              <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
                <p>
                  VAS Cyber is a leading cybersecurity, governance, and risk advisory firm dedicated to strengthening Africa's digital ecosystem.
                </p>
                <p>
                  Headquartered in Ghana, we support organizations across the continent with advanced security solutions, strategic IT governance, and evidence-based advisory services.
                </p>
                <p>
                  Our team consists of seasoned professionals with deep expertise in cybersecurity, forensics, payment system security, cloud governance, cyber program development, and technology leadership. We combine global standards, local regulatory expertise, and Pan-African insight to deliver reliable and relevant solutions.
                </p>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-2xl p-8 border-l-4 border-cyan-500">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-cyan-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-eye-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Our Vision</h3>
                    <p className="text-gray-700 leading-relaxed">
                      To become Africa's most trusted cybersecurity, governance, and digital risk management partner.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-2xl p-8 border-l-4 border-emerald-500">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-emerald-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-target-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Our Mission</h3>
                    <p className="text-gray-700 leading-relaxed">
                      To empower African enterprises with strong protection, regulatory compliance, and technical excellence built on integrity, intelligence, and innovation.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-gray-50 to-slate-50 rounded-2xl p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-star-line text-emerald-500"></i>
                  Our Values
                </h3>
                <div className="space-y-3">
                  {[
                    { label: 'Integrity', desc: 'Trustworthy and transparent in all engagements' },
                    { label: 'Excellence', desc: 'Premium delivery and continuous improvement' },
                    { label: 'Innovation', desc: 'Modern solutions for evolving threats' },
                    { label: 'Confidentiality', desc: 'Every client\'s security is sacred' },
                    { label: 'Leadership', desc: 'Transforming Africa\'s cybersecurity landscape' }
                  ].map((value, index) => (
                    <div key={index} className="flex items-start gap-3 bg-white rounded-lg p-3">
                      <i className="ri-checkbox-circle-fill text-emerald-500 text-xl flex-shrink-0 mt-0.5"></i>
                      <div>
                        <span className="font-semibold text-gray-900">{value.label}</span>
                        <span className="text-gray-600"> – {value.desc}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">Our Services</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive cybersecurity solutions tailored to your organization's needs
            </p>
          </div>

          <div className="space-y-6">
            {services.map((service, index) => (
              <div
                key={index}
                className={`bg-white rounded-2xl shadow-lg border-2 transition-all duration-300 overflow-hidden ${
                  expandedService === index ? 'border-emerald-500' : 'border-gray-200 hover:border-emerald-300'
                }`}
              >
                <div
                  className="flex items-center justify-between p-8 cursor-pointer"
                  onClick={() => setExpandedService(expandedService === index ? null : index)}
                >
                  <div className="flex items-center gap-6">
                    <div className="text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-br from-emerald-500 to-cyan-500 border-4 border-emerald-500 rounded-xl w-24 h-24 flex items-center justify-center">
                      {service.number}
                    </div>
                    <h3 className="text-3xl font-bold text-gray-900">{service.title}</h3>
                  </div>
                  <div className={`w-12 h-12 rounded-full border-2 border-emerald-500 flex items-center justify-center transition-transform duration-300 ${
                    expandedService === index ? 'rotate-45 bg-emerald-500' : 'bg-white'
                  }`}>
                    <i className={`ri-add-line text-2xl ${expandedService === index ? 'text-white' : 'text-emerald-500'}`}></i>
                  </div>
                </div>

                {expandedService === index && (
                  <div className="px-8 pb-8 border-t-2 border-gray-100 pt-8 bg-gradient-to-br from-gray-50 to-white">
                    <div className="border-l-4 border-emerald-500 pl-8">
                      <p className="text-lg text-gray-700 mb-6 leading-relaxed">{service.description}</p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                        {service.items.map((item, itemIndex) => (
                          <div key={itemIndex} className="flex items-start gap-3">
                            <i className="ri-checkbox-circle-fill text-emerald-500 text-xl flex-shrink-0 mt-0.5"></i>
                            <span className="text-gray-700">{item}</span>
                          </div>
                        ))}
                      </div>

                      <p className="text-xl italic text-emerald-600 font-medium mt-8">{service.tagline}</p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section id="industries" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">Industries We Serve</h2>
            <p className="text-xl text-gray-600">Protecting diverse sectors across Africa</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-8">
            {industries.map((industry, index) => (
              <div
                key={index}
                className="group flex flex-col items-center text-center cursor-pointer"
              >
                <div className="w-36 h-36 bg-white rounded-full shadow-lg flex items-center justify-center mb-4 group-hover:shadow-2xl group-hover:bg-gradient-to-br group-hover:from-emerald-500 group-hover:to-cyan-500 transition-all duration-300 group-hover:scale-105 border-2 border-gray-100 group-hover:border-transparent">
                  <i className={`${industry.icon} text-5xl text-gray-800 group-hover:text-white transition-colors duration-300`}></i>
                </div>
                <h3 className="text-base font-semibold text-gray-800 group-hover:text-emerald-600 transition-colors duration-300">
                  {industry.name}
                </h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section id="why-choose" className="py-24 bg-gradient-to-b from-emerald-50/30 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">Why Choose VAS Cyber?</h2>
            <p className="text-xl text-gray-600">What sets us apart in the cybersecurity landscape</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyChoose.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-10 shadow-md hover:shadow-xl transition-all duration-300 border-l-4 border-emerald-500"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-xl flex items-center justify-center mb-6">
                  <i className={`${item.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-gradient-to-br from-gray-900 to-gray-800">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-5xl font-bold text-white mb-4">Get In Touch</h2>
            <p className="text-xl text-gray-300">Ready to protect your business? Let's talk.</p>
          </div>

          <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="bg-white rounded-2xl p-10 shadow-2xl">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-emerald-500 focus:outline-none transition-colors text-sm"
                  placeholder="John Doe"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-emerald-500 focus:outline-none transition-colors text-sm"
                  placeholder="john@company.com"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="company" className="block text-sm font-semibold text-gray-700 mb-2">
                  Company Name
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-emerald-500 focus:outline-none transition-colors text-sm"
                  placeholder="Your Company"
                />
              </div>
              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-emerald-500 focus:outline-none transition-colors text-sm"
                  placeholder="+233 XX XXX XXXX"
                />
              </div>
            </div>

            <div className="mb-6">
              <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                Message *
              </label>
              <textarea
                id="message"
                name="message"
                required
                rows={6}
                maxLength={500}
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-emerald-500 focus:outline-none transition-colors resize-none text-sm"
                placeholder="Tell us about your security needs..."
              ></textarea>
              <div className="text-right text-xs text-gray-500 mt-1">
                {formData.message.length}/500 characters
              </div>
            </div>

            {formStatus === 'success' && (
              <div className="mb-6 p-4 bg-emerald-50 border-l-4 border-emerald-500 rounded-lg">
                <p className="text-emerald-700 font-semibold flex items-center gap-2">
                  <i className="ri-checkbox-circle-fill"></i>
                  Thank you! We'll get back to you shortly.
                </p>
              </div>
            )}

            {formStatus === 'error' && (
              <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg">
                <p className="text-red-700 font-semibold flex items-center gap-2">
                  <i className="ri-error-warning-fill"></i>
                  Something went wrong. Please try again.
                </p>
              </div>
            )}

            <button
              type="submit"
              disabled={formStatus === 'submitting'}
              className="w-full bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-bold py-4 rounded-lg hover:shadow-lg hover:shadow-emerald-500/50 transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {formStatus === 'submitting' ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  Sending...
                </>
              ) : (
                <>
                  Send Message <i className="ri-send-plane-fill"></i>
                </>
              )}
            </button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-16 relative overflow-hidden">
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 text-[180px] font-bold text-gray-800/10 whitespace-nowrap select-none pointer-events-none">
          VAS CYBER
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            <div>
              <img
                src="https://static.readdy.ai/image/3ca3a64fdbdd1a27f61611d5a6ab11d4/0b305659fb07b7246329e00bf7bbf5b5.png"
                alt="VAS Cyber"
                className="w-24 h-24 mb-4"
              />
              <p className="text-lg text-gray-400 mb-8">Securing Africa's Digital Future</p>
              <h3 className="text-2xl font-bold text-white mb-4">Ready to Protect Your Business?</h3>
              <a
                href="#contact"
                className="inline-block px-6 py-3 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-semibold rounded-full hover:shadow-lg hover:shadow-emerald-500/50 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Contact Us Today
              </a>
            </div>

            <div>
              <h4 className="text-sm font-bold uppercase tracking-wider text-gray-400 mb-6">Quick Links</h4>
              <ul className="space-y-4">
                {['Home', 'About', 'Services', 'Industries', 'Why Us', 'Contact'].map((link) => (
                  <li key={link}>
                    <a
                      href={`#${link.toLowerCase().replace(' ', '-')}`}
                      className="text-gray-300 hover:text-emerald-400 transition-colors duration-300 cursor-pointer"
                    >
                      {link}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-bold uppercase tracking-wider text-gray-400 mb-6">Services</h4>
              <ul className="space-y-4">
                {['Cyber Risk Management', 'Cybersecurity Services', 'IT Governance', 'Digital Forensics', 'Security Training', 'Advisory'].map((service) => (
                  <li key={service}>
                    <a
                      href="#services"
                      className="text-gray-300 hover:text-emerald-400 transition-colors duration-300 cursor-pointer"
                    >
                      {service}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-bold text-white mb-4">Newsletter</h4>
              <p className="text-gray-400 text-sm mb-4">
                Stay updated with the latest cybersecurity insights and VAS Cyber news.
              </p>
              <form 
                id="newsletter-form"
                data-readdy-form
                onSubmit={handleNewsletterSubmit}
                className="flex gap-2"
              >
                <input
                  type="email"
                  name="email"
                  placeholder="Your email"
                  required
                  className="flex-1 px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500 transition-colors text-sm"
                />
                <button
                  type="submit"
                  disabled={newsletterStatus?.type === 'loading'}
                  className="w-12 h-12 bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-full flex items-center justify-center hover:shadow-lg hover:shadow-emerald-500/50 transition-all duration-300 flex-shrink-0 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {newsletterStatus?.type === 'loading' ? (
                    <i className="ri-loader-4-line text-white text-xl animate-spin"></i>
                  ) : (
                    <i className="ri-arrow-right-line text-white text-xl"></i>
                  )}
                </button>
              </form>
              {newsletterStatus && (
                <p className={`text-sm mt-2 ${
                  newsletterStatus.type === 'success' ? 'text-emerald-400' : 
                  newsletterStatus.type === 'error' ? 'text-red-400' : 'text-gray-400'
                }`}>
                  {newsletterStatus.message}
                </p>
              )}
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-400">
              © 2025 VAS Cyber. All rights reserved. Fortifying the Digital Frontier.
            </p>
            <div className="flex gap-6">
              <Link
                to="/privacy"
                className="text-sm text-gray-400 hover:text-emerald-400 transition-colors duration-300 cursor-pointer"
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms"
                className="text-sm text-gray-400 hover:text-emerald-400 transition-colors duration-300 cursor-pointer"
              >
                Terms of Service
              </Link>
              <a
                href="https://readdy.ai/?origin=logo"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-gray-400 hover:text-emerald-400 transition-colors duration-300 cursor-pointer"
              >
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
